import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Archive
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import archivedContractService from '../../services/archivedContractService'

const ArchivedContractView = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const contractData = location.state?.contractData

  const [contract, setContract] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (contractData?._id) {
      loadContractData(contractData._id)
    } else {
      toast.error('Contract data not found')
      navigate('/contracts/archived-contract')
    }
  }, [contractData])

  const loadContractData = async (id) => {
    try {
      setIsLoading(true)
      const response = await archivedContractService.getArchivedContractById(id)
      if (response.success && response.data) {
        setContract(response.data)
      } else {
        toast.error('Failed to load archived contract')
        navigate('/contracts/archived-contract')
      }
    } catch (error) {
      console.error('Error loading archived contract:', error)
      toast.error('Failed to load archived contract')
      navigate('/contracts/archived-contract')
    } finally {
      setIsLoading(false)
    }
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Contracts', path: '/contracts' },
    { label: 'Archived Contracts', path: '/contracts/archived-contract' },
    { label: 'View Archived Contract', path: location.pathname, isCurrent: true }
  ]

  if (isLoading) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading archived contract...</p>
        </div>
      </div>
    )
  }

  if (!contract) {
    return null
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>View Archived Contract</h1>
          <p>Archived contract details and information</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/contracts/archived-contract')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        {/* Contract Information */}
        <div className="sac-form-header">
          <h2>Contract Information</h2>
        </div>
          
        <div className="form-content">
          <div className="grid-row-4">
            <div className="form-group">
              <label className="form-label">Contract Number</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057' }}>
                {contract.contractNumber}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Contract Title</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057' }}>
                {contract.contractTitle}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Contract Type</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057' }}>
                {contract.contractType}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Party Name</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057' }}>
                {contract.partyName}
              </div>
            </div>
          </div>

          {contract.contractDescription && (
            <div className="form-group">
              <label className="form-label">Contract Description</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057', minHeight: '60px', padding: '12px' }}>
                {contract.contractDescription}
              </div>
            </div>
          )}

          {/* Archive Information */}
          <div className="sac-form-header" style={{ marginTop: '24px' }}>
            <h2>Archive Information</h2>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label className="form-label">Archive Date</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057' }}>
                {contract.archiveDate ? new Date(contract.archiveDate).toLocaleDateString() : '-'}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Archived By</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057' }}>
                {contract.archivedByName || contract.archivedBy?.firstName || '-'}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Archive Reason</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057' }}>
                {contract.archiveReason}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Original Status</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057' }}>
                {contract.originalStatus || '-'}
              </div>
            </div>
          </div>

          {contract.archiveReasonDetails && (
            <div className="form-group">
              <label className="form-label">Archive Reason Details</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057', minHeight: '60px', padding: '12px' }}>
                {contract.archiveReasonDetails}
              </div>
            </div>
          )}

          {/* Contract Period */}
          <div className="sac-form-header" style={{ marginTop: '24px' }}>
            <h2>Contract Period</h2>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057' }}>
                {contract.contractStartDate ? new Date(contract.contractStartDate).toLocaleDateString() : '-'}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">End Date</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057' }}>
                {contract.contractEndDate ? new Date(contract.contractEndDate).toLocaleDateString() : '-'}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Duration (Months)</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057' }}>
                {contract.contractDuration || '-'}
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div className="sac-form-header" style={{ marginTop: '24px' }}>
            <h2>Financial Information</h2>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label className="form-label">Contract Value</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057', fontWeight: '600' }}>
                {formatCurrency(contract.contractValue, contract.currency)}
              </div>
            </div>
          </div>

          {/* Contract Summary */}
          {contract.contractSummary && Object.keys(contract.contractSummary).length > 0 && (
            <>
              <div className="sac-form-header" style={{ marginTop: '24px' }}>
                <h2>Contract Summary</h2>
              </div>

              <div className="grid-row-4">
                {contract.contractSummary.totalValue !== undefined && (
                  <div className="form-group">
                    <label className="form-label">Total Value</label>
                    <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057' }}>
                      {formatCurrency(contract.contractSummary.totalValue, contract.currency)}
                    </div>
                  </div>
                )}
                {contract.contractSummary.totalPayments !== undefined && (
                  <div className="form-group">
                    <label className="form-label">Total Payments</label>
                    <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057' }}>
                      {formatCurrency(contract.contractSummary.totalPayments, contract.currency)}
                    </div>
                  </div>
                )}
                {contract.contractSummary.performanceRating !== undefined && (
                  <div className="form-group">
                    <label className="form-label">Performance Rating</label>
                    <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057' }}>
                      {contract.contractSummary.performanceRating}/10
                    </div>
                  </div>
                )}
              </div>

              {contract.contractSummary.finalNotes && (
                <div className="form-group">
                  <label className="form-label">Final Notes</label>
                  <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057', minHeight: '60px', padding: '12px' }}>
                    {contract.contractSummary.finalNotes}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Retention Information */}
          <div className="sac-form-header" style={{ marginTop: '24px' }}>
            <h2>Retention Information</h2>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label className="form-label">Retention Period (Years)</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057' }}>
                {contract.retentionPeriod || 7}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Retention End Date</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057' }}>
                {contract.retentionEndDate ? new Date(contract.retentionEndDate).toLocaleDateString() : '-'}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Can Be Deleted</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057' }}>
                {contract.canBeDeleted ? (
                  <span className="status-badge danger">Yes</span>
                ) : (
                  <span className="status-badge inactive">No</span>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          {contract.notes && (
            <div className="form-group">
              <label className="form-label">Notes</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057', minHeight: '60px', padding: '12px' }}>
                {contract.notes}
              </div>
            </div>
          )}

          {contract.internalNotes && (
            <div className="form-group">
              <label className="form-label">Internal Notes</label>
              <div className="form-input" style={{ backgroundColor: '#f9fafb', color: '#495057', minHeight: '60px', padding: '12px' }}>
                {contract.internalNotes}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ArchivedContractView

